//internal-deps
import {
    ICouponsAPIResponse,
    ICouponCard,
    ICouponCards,
    INextBestCoupon,
    CouponListingPLWidgetTypes,
    CouponMessageCard,
} from "@swiggy-private/cart-types";
import { useMountedRef, captureError } from "@swiggy-private/core-ui";
import { ApiContext, CheckoutActionTypes, CheckoutContext } from "@swiggy-private/checkout-core";
import type { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";
import { useCallback, useContext, useRef, useState } from "react";
// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
// helpers
import { isCouponNotApplied } from "@cart-client/helpers/Cart";
import { generateNativeLocalGiftingCartFromStates } from "@cart-client/helpers/GiftingCart";
import { randomStringIdGenerator } from "@swiggy-private/core-ui";
// apis
import { fetchGiftingCouponListing, updateGiftingCoupons, ResultCartData } from "@cart-client/apis/GiftingCart";
// types
import { CartActionTypes } from "@cart-client/reducers/CartReducer";
import { IFetchCouponsParams } from "@cart-client/hooks/types";
import { SyncApplyCouponCartParams } from "@cart-client/interfaces/Coupon";
// constants
import { ERROR_TEXT } from "@cart-client/constants/Coupon";

// Scaffolding for gifting coupon logic matching useApplyCoupon interface
// All methods and state are stubbed with clear signatures and comments for future implementation

export default function useGiftingApplyCoupon() {
    // State scaffolding
    const hasInitiallyFetched = useRef<boolean>(false);
    const { cartState, setCartState } = useContext(CartContext);
    const apiContextValue = useContext(ApiContext);
    const { setCheckoutState } = useContext(CheckoutContext);
    const [isFetching, setIsFetching] = useState<boolean>(false);
    const [isApplyingCoupon, setIsApplyingCoupon] = useState<boolean>(false);
    const [showCouponModal, setShowCouponModal] = useState<boolean>(false);
    const [showDelightPopup, setShowDelightPopup] = useState<boolean>(false);
    const [coupons, setCoupons] = useState<ICouponCard[] | null>(null);
    const [nextBestCoupon, setNextBestCoupon] = useState<INextBestCoupon | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [cards, setCards] = useState<IPLWidgetControllerCards[]>([]);
    const [updateCouponState, setUpdateCouponState] = useState<boolean>(false);
    const apiHost = apiContextValue.host.unwrap();

    const apiCallRef = useRef(false);
    const isMountedRef = useMountedRef();

    const apiSuccess = useCallback(
        (response: ResultCartData): CouponMessageCard | undefined => {
            apiCallRef.current = false;

            if (isMountedRef.current) {
                const cartData = response.unwrap() ?? null;
                if (cartData.data) {
                    if (isCouponNotApplied(cartData.data)) {
                        return cartData.data.coupon_details?.coupon_error_card;
                    }

                    setCartState({
                        type: CartActionTypes.UPDATE_CART,
                        payload: {
                            uniqueIdForCart: randomStringIdGenerator(),
                            localCart:
                                (cartData.data &&
                                    generateNativeLocalGiftingCartFromStates(cartData.data, cartState?.localCart)) ||
                                null,
                            reviewedCart: cartData,
                        },
                    });
                }
            }
        },
        [isMountedRef, setCartState, cartState],
    );

    const clearErrorMessage = useCallback(() => {
        setErrorMessage("");
    }, []);

    const resetCouponListingPage = useCallback(() => {
        setIsApplyingCoupon(false);
        clearErrorMessage();
        setCoupons(null);
        setIsFetching(false);
        setNextBestCoupon(null);
        hasInitiallyFetched.current = false;
    }, [clearErrorMessage, setIsApplyingCoupon, setIsFetching, setNextBestCoupon, setCoupons]);

    const apiFailure = useCallback(
        (err: unknown, isCouponListingFlow?: boolean) => {
            apiCallRef.current = false;

            if (isMountedRef.current) {
                captureError(err);
                setUpdateCouponState(false);
                setShowCouponModal(false);
                if (isCouponListingFlow) {
                    setErrorMessage(ERROR_TEXT.DEFAULT);
                    setIsApplyingCoupon(false);
                }
            }
        },
        [isMountedRef],
    );

    const syncApplyCouponCart = useCallback(
        ({ appliedCouponCodes, couponCode, isCouponListingFlow }: SyncApplyCouponCartParams) => {
            if (!isMountedRef.current || updateCouponState || apiCallRef.current) {
                return;
            }

            apiCallRef.current = true;
            setIsApplyingCoupon(true);
            setUpdateCouponState(true);

            const trimmedCouponCode = couponCode?.trim();

            updateGiftingCoupons({
                apiHost,
                appliedCouponCodes: appliedCouponCodes || [],
                couponCode: trimmedCouponCode || "",
                localCart: cartState?.localCart,
            })
                .then(apiSuccess)
                .then((couponError?: CouponMessageCard) => {
                    if (isMountedRef.current) {
                        if (isCouponListingFlow) {
                            if (!couponError) {
                                setShowCouponModal(true);
                                setUpdateCouponState(false);
                                resetCouponListingPage();
                                setCheckoutState({
                                    type: CheckoutActionTypes.SHOW_GIFTING_CART,
                                    payload: {
                                        skipCartReload: true,
                                    },
                                });
                            } else {
                                setIsApplyingCoupon(false);
                                setUpdateCouponState(false);
                            }
                        } else {
                            setShowCouponModal(true);
                            setUpdateCouponState(false);
                        }
                    }
                })
                .catch(apiFailure);
        },
        [
            isMountedRef,
            updateCouponState,
            apiHost,
            cartState?.localCart,
            apiSuccess,
            apiFailure,
            resetCouponListingPage,
            setCheckoutState,
        ],
    );

    // Method: Toggle the coupon modal visibility
    const toggleCouponModal = useCallback(() => {
        setShowCouponModal((prev) => !prev);
    }, []);

    const togglingDelightPopup = useCallback(() => {
        setShowDelightPopup((showDelightPopup) => !showDelightPopup);
    }, []);

    const extractCouponCardsFromResponse = useCallback((response: ICouponsAPIResponse) => {
        const filterCouponCardsFromResponse = (response?.success?.cards as unknown as ICouponCards[])?.filter(
            (each: ICouponCards) => each?.card?.card?.["@type"] === CouponListingPLWidgetTypes.COUPON_CARD,
        ) as ICouponCards[];
        const couponGroupArr = filterCouponCardsFromResponse?.map((each: ICouponCards) => each?.card?.card);
        couponGroupArr && setCoupons(couponGroupArr);
    }, []);

    const extractPLWidgetsFromResponse = useCallback(
        (response: ICouponsAPIResponse) => {
            extractCouponCardsFromResponse(response);
            setCards(response.success?.cards || []);
        },
        [extractCouponCardsFromResponse],
    );

    // Method: Fetch available coupons for the gifting cart
    const fetchCoupons = useCallback(
        ({ appliedCouponCodes }: IFetchCouponsParams) => {
            setIsFetching(true);
            hasInitiallyFetched.current = true;

            fetchGiftingCouponListing(apiHost, { appliedCouponCodes })
                .then((response: ICouponsAPIResponse) => {
                    setIsFetching(false);
                    extractPLWidgetsFromResponse(response);
                })
                .catch((error: unknown) => {
                    setIsFetching(false);
                    captureError(error);
                });
        },
        [apiHost, extractPLWidgetsFromResponse],
    );

    return {
        toggleCouponModal,
        showCouponModal,
        updateCouponState,
        syncApplyCouponCart,
        fetchCoupons,
        isFetching,
        isApplyingCoupon,
        coupons,
        nextBestCoupon,
        errorMessage,
        clearErrorMessage,
        resetCouponListingPage,
        togglingDelightPopup,
        showDelightPopup,
        cards,
        hasInitiallyFetched: hasInitiallyFetched.current,
    };
}
